import * as maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { useEffect, useRef, useState } from "react";
import type { Locale, TrackingSnapshot } from "./domain";

const MAP_STYLE: maplibregl.StyleSpecification = {
  version: 8,
  sources: {
    osm: {
      type: "raster",
      tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
      tileSize: 256,
      attribution: "© OpenStreetMap contributors",
      maxzoom: 19,
    },
  },
  layers: [{ id: "osm", type: "raster", source: "osm" }],
};

const ROUTE_SOURCE = "route";
const ROUTE_LAYER = "route-line";

function pinElement(kind: "courier" | "target") {
  const el = document.createElement("div");
  el.className = `map-pin map-pin-${kind}`;
  el.innerHTML =
    kind === "courier"
      ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 2 11 13"/><path d="M22 2 15 22l-4-9-9-4Z"/></svg>'
      : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z"/><path d="M9 22V12h6v10"/></svg>';
  return el;
}

export function LiveTrackingMap({
  locale,
  snapshot,
}: {
  locale: Locale;
  snapshot: TrackingSnapshot | null;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const courierMarkerRef = useRef<maplibregl.Marker | null>(null);
  const targetMarkerRef = useRef<maplibregl.Marker | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || mapRef.current) return;
    let map: maplibregl.Map;
    try {
      map = new maplibregl.Map({
        container,
        style: MAP_STYLE,
        center: [10.18, 36.8],
        zoom: 11,
        attributionControl: { compact: true },
      });
    } catch {
      setFailed(true);
      return;
    }
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-left");
    map.on("load", () => {
      map.addSource(ROUTE_SOURCE, {
        type: "geojson",
        data: {
          type: "Feature",
          properties: {},
          geometry: { type: "LineString", coordinates: [] },
        },
      });
      map.addLayer({
        id: ROUTE_LAYER,
        type: "line",
        source: ROUTE_SOURCE,
        layout: { "line-cap": "round", "line-join": "round" },
        paint: { "line-color": "#d97706", "line-width": 4, "line-opacity": 0.9 },
      });
    });
    mapRef.current = map;
    return () => {
      courierMarkerRef.current?.remove();
      targetMarkerRef.current?.remove();
      courierMarkerRef.current = null;
      targetMarkerRef.current = null;
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !snapshot) return;
    const apply = () => {
      const route = snapshot.route;
      const source = map.getSource(ROUTE_SOURCE) as maplibregl.GeoJSONSource | undefined;
      if (source) {
        source.setData({
          type: "Feature",
          properties: {},
          geometry: {
            type: "LineString",
            coordinates: route ? route.geometry.map((p) => [p.longitude, p.latitude]) : [],
          },
        });
      }
      const target = route?.geometry.at(-1);
      if (target) {
        if (!targetMarkerRef.current) {
          targetMarkerRef.current = new maplibregl.Marker({ element: pinElement("target") })
            .setLngLat([target.longitude, target.latitude])
            .addTo(map);
        } else {
          targetMarkerRef.current.setLngLat([target.longitude, target.latitude]);
        }
      } else {
        targetMarkerRef.current?.remove();
        targetMarkerRef.current = null;
      }
      const courier = snapshot.courierLocation;
      if (courier) {
        if (!courierMarkerRef.current) {
          courierMarkerRef.current = new maplibregl.Marker({
            element: pinElement("courier"),
            rotationAlignment: "map",
          })
            .setLngLat([courier.longitude, courier.latitude])
            .addTo(map);
        } else {
          courierMarkerRef.current.setLngLat([courier.longitude, courier.latitude]);
        }
        courierMarkerRef.current.setRotation(courier.headingDegrees ?? 0);
      } else {
        courierMarkerRef.current?.remove();
        courierMarkerRef.current = null;
      }
      const bounds = new maplibregl.LngLatBounds();
      if (courier) bounds.extend([courier.longitude, courier.latitude]);
      if (target) bounds.extend([target.longitude, target.latitude]);
      if (!bounds.isEmpty()) {
        map.fitBounds(bounds, { padding: 72, maxZoom: 15, duration: 600 });
      }
    };
    if (map.loaded()) apply();
    else map.once("load", apply);
  }, [snapshot]);

  if (failed) {
    return (
      <div className="map-placeholder">
        <span className="route-dot store" />
        <span className="route-line" />
        <span className="route-dot home" />
        <b>
          {locale === "ar"
            ? "يظهر موقع السائق بعد استلام الطلب"
            : "Position visible après le retrait"}
        </b>
      </div>
    );
  }

  return (
    <div className="live-map" dir="ltr">
      <div ref={containerRef} className="live-map-canvas" />
      {!snapshot?.courierLocation && (
        <div className="map-placeholder live-map-overlay">
          <span className="route-dot store" />
          <span className="route-line" />
          <span className="route-dot home" />
          <b>
            {locale === "ar"
              ? "يظهر موقع السائق بعد استلام الطلب"
              : "Position visible après le retrait"}
          </b>
        </div>
      )}
      <b>{locale === "ar" ? "تتبع مباشر" : "Suivi en direct"}</b>
    </div>
  );
}
